import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { formatDateLocal } from '../../../../../lib/script'
import Button from '../../../../common/Button'
import classes from './PoliceReportDetails.module.css'
import PatientDetailsHeader from "../../../../patient/page/PatientDetailsHeader";
import Card from "../../../../common/Card";
import { caseQuickUpdate } from "../../../../../lib/fetchApi";
import { notificationActions } from "../../../../../store/notifications";
import DownloadDocument from "../../../../patient/details/procodocs/documents/controls/DownloadDocument";
import { caseActions } from "../../../../../store/case";
import { getDaysDifferenceAbs } from "../../../../../lib/dateScripts";
import IncidentDetails from "./sections/IncidentDetails";
import PatientDemographics from "./sections/PatientDemographics";
import PatientVehicleInsurances from "./sections/PatientVehicleInsurances";
import AtFaultInsurances from "./sections/AtFaultInsurances";
import DemographicsDetails from "./sections/DemographicsDetails";
import { NGvsCaseid } from "../../../../../layout/NGvsCaseid";
import { policeReportFunctions } from "../../../../../lib/policeReport";

export default function PoliceReportDetailsInner() {
    const dispatch = useDispatch()
    const activeCase = useSelector(state => state.case.activeCase)
    const userToken = useSelector(state => state.user.token)
    const selectedWorkQueue = useSelector((state) => state.workqueue.selectedWorkQueue);

    const [activeCaseData, setActiveCaseData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [confirmOpen, setConfirmOpen] = useState(false)

    const policeReportCaseDocument = activeCase?.police_report?.case_document
    const isProcessed = policeReportFunctions.isMoonbirdReportProcessed(activeCase)

    function downloadUnavailableHandler() {
        dispatch(
            notificationActions.setError({
                errorMessage: "No police report document available for this case.",
            })
        )
    }

    const workqSlug = selectedWorkQueue?.slug || "police-report"

    function openMarkCompleteConfirm() {
        if (isProcessed) {
            return
        }
        setConfirmOpen(true)
    }

    async function executeMarkAsComplete() {
        setLoading(true)

        const reqBody = {
            type: "case_police_report",
            type_id: null,
            workq: workqSlug,
            fields: {
                is_moonbird_report_processed: 1,
            },
        }

        const update = await caseQuickUpdate(userToken, activeCase.case.id, reqBody, null, true)

        if (update && update.errorMessage) {
            dispatch(notificationActions.setError(update))
        } else {
            setConfirmOpen(false)
            dispatch(
                caseActions.modifyActiveCaseMultipleObject({
                    folder: "police_report",
                    item: {
                        fields: [{ field: "is_moonbird_report_processed", value: 1 }],
                    },
                })
            )
            if (activeCase.case_police_report) {
                dispatch(
                    caseActions.modifyActiveCaseMultipleObject({
                        folder: "case_police_report",
                        item: {
                            fields: [{ field: "is_moonbird_report_processed", value: 1 }],
                        },
                    })
                )
            }
        }

        setLoading(false)
    }

    useEffect(() => {
        if (!activeCase) {
            setActiveCaseData(null)
            return
        }

        const filterActiveItems = (items) => {
            if (!Array.isArray(items)) {
                return items
            }

            return items.filter((item) => Number(item?.active) === 1)
        }

        setActiveCaseData({
            ...activeCase,
            liabilities: filterActiveItems(activeCase.liabilities),
            mp_pip_um: filterActiveItems(activeCase.mp_pip_um),
        })
    }, [activeCase])

    const overlayRoot = typeof document !== "undefined" ? document.getElementById("overlay-root") : null

    const markCompleteConfirmModal =
        confirmOpen && overlayRoot
            ? ReactDOM.createPortal(
                  <div
                      className={classes.confirm_overlay}
                      role="presentation"
                      onClick={() => !loading && setConfirmOpen(false)}
                  >
                      <div
                          className={classes.confirm_card}
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="mark-complete-confirm-title"
                          onClick={(e) => e.stopPropagation()}
                      >
                          <h3 id="mark-complete-confirm-title" className={classes.confirm_title}>
                              Mark as complete?
                          </h3>
                          <p className={classes.confirm_text}>
                              This will mark the Moonbird police report as processed. Copy and edit actions on this
                              screen will be disabled.
                          </p>
                          <div className={classes.confirm_actions}>
                              <Button
                                  click={() => !loading && setConfirmOpen(false)}
                                  btnStyle="btn_white"
                                  isDisabled={loading}
                              >
                                  Cancel
                              </Button>
                              <Button click={executeMarkAsComplete} isLoading={loading}>
                                  Confirm
                              </Button>
                          </div>
                      </div>
                  </div>,
                  overlayRoot
              )
            : null

    return <>
        {markCompleteConfirmModal}
        {activeCaseData && <div className={classes.police_report_details}>
            <PatientDetailsHeader />
            <div className={classes.police_report_details_inner}>
                <Card>
                    <div className={classes.police_report_details_inner_wrapper}>
                        <div className={classes.report_info}>
                            <div>
                                <div className={'flex-container'}>
                                    {policeReportCaseDocument ? (
                                        <DownloadDocument
                                            userToken={userToken}
                                            activeCase={activeCase}
                                            item={policeReportCaseDocument}
                                            btnStyle="btn_simple"
                                            docType="document"
                                        >
                                            <h2>Police Report Information</h2> <i className="ti ti-download"></i>
                                        </DownloadDocument>
                                    ) : (
                                        <Button
                                            btnStyle="btn_simple"
                                            title="Download police report"
                                            click={downloadUnavailableHandler}
                                        >
                                            <h2>Police Report Information</h2> <i className="ti ti-download"></i>
                                        </Button>
                                    )}
                                </div>

                                <div className={classes.report_info_group}>
                                    <div><strong>Report Type: </strong>{activeCase.police_report?.report_type}</div>
                                    <div><strong>Drivers License #: </strong>{activeCase.police_report?.drivers_license}</div>
                                    <div><strong>Case Description: </strong>{activeCase.police_report?.case_description}</div>
                                    <div><strong>Agency Case Number: </strong>{activeCase.police_report?.agency_case_number}</div>
                                </div>

                                <div className={classes.report_info_group}>
                                    <div><strong>Report Number: </strong>{activeCase.police_report?.report_number}</div>
                                    <div><strong>Report State: </strong>{activeCase.police_report?.report_state}</div>
                                    <div><strong>Report Created At: </strong>{formatDateLocal(activeCase.police_report?.police_report_record_created_at)} ({getDaysDifferenceAbs(activeCase.police_report?.police_report_record_created_at)} days since TX Start)</div>
                                </div>
                            </div>

                            <div className={classes.controls}>
                                <NGvsCaseid noWorkq={true} caseid={activeCase.case?.id} person_number={activeCase?.patient?.person_number}><i className="ti ti-link"></i> Case Details</NGvsCaseid>
                                {isProcessed ? (
                                    <span className={classes.processed_badge} title="This police report has been marked as processed">
                                        <i className="ti ti-check" aria-hidden />
                                        Processed
                                    </span>
                                ) : (
                                    <Button click={openMarkCompleteConfirm} isLoading={loading}>
                                        <i className="ti ti-check"></i>Mark as Complete
                                    </Button>
                                )}
                            </div>
                        </div>

                        <div className={classes.group_header}>
                            <div className={classes.group_header_cell}></div>
                            <div className={classes.group_header_cell}>
                                <strong>Police Report Values</strong>
                            </div>
                            <div className={classes.status}></div>
                            <div className={classes.group_header_cell}>
                                <strong>Curent eCM Values</strong>
                            </div>
                        </div>

                        <PatientDemographics classes={classes} activeCase={activeCaseData} isProcessed={isProcessed} />
                        <DemographicsDetails classes={classes} activeCase={activeCaseData} isProcessed={isProcessed} />
                        <IncidentDetails classes={classes} activeCase={activeCaseData} isProcessed={isProcessed} />
                        <PatientVehicleInsurances classes={classes} activeCase={activeCaseData} isProcessed={isProcessed} />
                        <AtFaultInsurances classes={classes} activeCase={activeCaseData} isProcessed={isProcessed} />
                    </div>
                </Card>
            </div>
        </div>}
    </>
}
